import {
  computed, defineComponent, markRaw, nextTick, provide, reactive, ref,
  shallowReactive, watch, type Component, type PropType, type Ref, type VNode,
} from "vue";
import {
  isNavigationFailure, routeLocationKey, useRoute, useRouter,
  type RouteLocationNormalizedLoaded,
} from "vue-router";

export interface WorkspaceTab {
  path: string;
  fullPath: string;
  title: string;
  cacheName: string;
  scrollTop: number;
}

// Keep the original lifecycle of pages that own leave confirmations or drafts.
const uncachedPages = new Set([
  "issue-create", "contract-create", "contract-edit", "meeting-detail", "personal-report-editor",
]);
const sidebarPreference = "pms-sidebar-collapsed";

function initialCollapsed() {
  try {
    const saved = window.localStorage.getItem(sidebarPreference);
    if (saved !== null) return saved === "true";
  } catch { /* Browser storage may be disabled. */ }
  return window.innerWidth <= 1180;
}

function createPageHost(name: string) {
  return markRaw(defineComponent({
    name,
    props: {
      page: { type: Object as PropType<VNode>, required: true },
      route: { type: Object as PropType<RouteLocationNormalizedLoaded>, required: true },
    },
    setup(props) {
      // Inactive pages must not read another tab's global route/query/ID.
      const pageRoute = shallowReactive({ ...props.route });
      provide(routeLocationKey, pageRoute);
      watch(() => props.route, (value) => Object.assign(pageRoute, value));
      return () => props.page;
    },
  }));
}

export function useWorkspaceTabs(main: Ref<HTMLElement | undefined>) {
  const route = useRoute();
  const router = useRouter();
  const collapsed = ref(initialCollapsed());
  const tabs = ref<WorkspaceTab[]>([
    { path: "/dashboard", fullPath: "/dashboard", title: "工作台", cacheName: "WorkspaceHome", scrollTop: 0 },
  ]);
  const hosts = new Map<string, Component>();
  const cachedNames = reactive(new Set<string>(["WorkspaceHome"]));
  const cacheNames = computed(() => [...cachedNames]);
  let nextId = 0;
  let previousPath: string | undefined;
  let closing = false;

  function pageHost(viewRoute: RouteLocationNormalizedLoaded) {
    let host = hosts.get(viewRoute.path);
    if (!host) {
      const tab = tabs.value.find((item) => item.path === viewRoute.path);
      host = createPageHost(tab?.cacheName ?? `WorkspacePage${++nextId}`);
      hosts.set(viewRoute.path, host);
    }
    return host;
  }

  watch(() => route.fullPath, async () => {
    const previous = tabs.value.find((item) => item.path === previousPath);
    if (previous && main.value) previous.scrollTop = main.value.scrollTop;
    let tab = tabs.value.find((item) => item.path === route.path);
    if (!tab) {
      const title = String(route.meta.title ?? "页面");
      const id = route.params.id;
      tab = {
        path: route.path, fullPath: route.fullPath,
        title: id ? `${title} · ${String(id).slice(-6)}` : title,
        cacheName: `WorkspacePage${++nextId}`, scrollTop: 0,
      };
      tabs.value.push(tab);
    }
    tab.fullPath = route.fullPath;
    if (!uncachedPages.has(String(route.name))) cachedNames.add(tab.cacheName);
    previousPath = route.path;
    const activePath = route.path;
    await nextTick();
    if (main.value && route.path === activePath) main.value.scrollTop = tab.scrollTop;
  }, { immediate: true });

  async function selectTab(tab: WorkspaceTab) {
    // router.onError owns recovery for failed lazy imports.
    return router.push(tab.fullPath).catch(() => undefined);
  }

  async function closeTab(tab: WorkspaceTab) {
    if (tab.path === "/dashboard" || closing) return;
    closing = true;
    try {
      const index = tabs.value.indexOf(tab);
      if (index < 0) return;
      if (route.path === tab.path) {
        const fallback = tabs.value[index - 1] ?? tabs.value[index + 1];
        if (!fallback) return;
        const result = await selectTab(fallback);
        if (isNavigationFailure(result) || route.path === tab.path) return;
      }
      tabs.value = tabs.value.filter((item) => item.path !== tab.path);
      cachedNames.delete(tab.cacheName);
      hosts.delete(tab.path);
    } finally {
      closing = false;
    }
  }

  function toggleSidebar() {
    collapsed.value = !collapsed.value;
    try {
      window.localStorage.setItem(sidebarPreference, String(collapsed.value));
    } catch { /* The toggle still works without persistent storage. */ }
  }

  return { tabs, collapsed, cacheNames, pageHost, selectTab, closeTab, toggleSidebar };
}
